import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { IconButton } from "./IconButton";
import { Button, inputCls } from "./ui";

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),audio[controls],video[controls],[tabindex]:not([tabindex="-1"])';
const FIELD = "input:not([disabled]):not([type=hidden]):not([type=checkbox]):not([type=radio]),select:not([disabled]),textarea:not([disabled])";

/** The layers open now, newest last: only the top one answers Escape and keeps Tab inside it. */
const layers: RefObject<HTMLElement | null>[] = [];

const visible = (el: HTMLElement) => el.getClientRects().length > 0;

/**
 * What every layer over the page does (WAI-ARIA's modal dialog pattern): it is put on top of the stack, focus moves in
 * (an element marked `data-autofocus`, else the first field — not on a touch screen, where it would throw up the
 * keyboard — else the least destructive action, else the panel) and goes back to whatever opened it when it closes, Tab
 * cycles inside it, Escape closes it, and the page behind does not scroll. A dropdown inside that stops its own Escape
 * keeps the layer open. Dialog, Overlay and Drawer all use it.
 */
export function useDialogLayer(open: boolean, onClose: () => void, panel: RefObject<HTMLElement | null>) {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return;
    layers.push(panel);
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const el = panel.current;
    // A field the screen focused itself (React's autoFocus) keeps the focus.
    if (el && !el.contains(document.activeElement)) {
      const touch = window.matchMedia("(pointer: coarse)").matches;
      const first =
        el.querySelector<HTMLElement>("[data-autofocus]") ??
        (touch ? null : el.querySelector<HTMLElement>(FIELD)) ??
        el.querySelector<HTMLElement>("[data-dialog-cancel]") ??
        el;
      first.focus({ preventScroll: true });
    }
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (layers[layers.length - 1] !== panel || e.defaultPrevented) return;
      const root = panel.current;
      if (e.key === "Escape") {
        e.preventDefault();
        close.current();
      } else if (e.key === "Tab" && root) {
        const items = [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(visible);
        const active = document.activeElement;
        if (items.length === 0) {
          e.preventDefault();
          root.focus();
        } else if (e.shiftKey && (active === items[0] || !root.contains(active))) {
          e.preventDefault();
          items[items.length - 1]!.focus();
        } else if (!e.shiftKey && (active === items[items.length - 1] || !root.contains(active))) {
          e.preventDefault();
          items[0]!.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      layers.splice(layers.indexOf(panel), 1);
      document.body.style.overflow = overflow;
      // Back to what opened it; when that is gone (the dialog it sat in closed too), to the layer now on top.
      if (opener?.isConnected) opener.focus({ preventScroll: true });
      else layers[layers.length - 1]?.current?.focus({ preventScroll: true });
    };
  }, [open, panel]);
}

/**
 * The one pop-up of the platform: a title with an optional line under it, the body, and a footer that always reads the
 * same way — `secondary` on the left (Delete, Rotate key), then Cancel, then `primary` on the right. A full-screen
 * sheet below 640px, a card from there up. Escape and the backdrop close it, focus is kept inside and handed back.
 * `cancel` is "Cancel" whenever there is a primary action (`false` hides it, for a dialog whose primary is "Done");
 * `busy` disables it while a save runs. `headerAction` is the item's own on/off switch, beside the close button.
 */
export function Dialog({
  open = true,
  onClose,
  title,
  description,
  headerAction,
  size = "md",
  primary,
  secondary,
  cancel,
  busy = false,
  children,
}: {
  open?: boolean;
  onClose: () => void;
  title: ReactNode;
  /** One line under the title: what the dialog is for, or what confirming does. */
  description?: ReactNode;
  headerAction?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  /** The main action, rightmost; two when a dialog genuinely offers two ways to go on. */
  primary?: ReactNode;
  /** Actions on the left, apart from the main one: Delete, Rotate key. */
  secondary?: ReactNode;
  cancel?: string | false;
  busy?: boolean;
  children?: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  useDialogLayer(open, onClose, panel);
  if (!open) return null;
  const width = { sm: "sm:max-w-md", md: "sm:max-w-lg", lg: "sm:max-w-2xl", xl: "sm:max-w-4xl" }[size];
  const cancelLabel = cancel ?? (primary ? "Cancel" : false);

  return createPortal(
    <div className="fixed inset-0 z-50 flex sm:items-center sm:justify-center sm:p-6">
      <div className="absolute inset-0 hidden bg-black/25 backdrop-blur-sm sm:block" aria-hidden="true" onClick={onClose} />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={`animate-fade-up relative flex h-full w-full flex-col bg-surface shadow-2xl outline-none sm:h-auto sm:max-h-[90vh] sm:rounded-2xl sm:ring-1 sm:ring-line ${width}`}
      >
        <div className="flex shrink-0 items-start gap-3 border-b border-line px-5 pt-[max(0.875rem,env(safe-area-inset-top))] pb-3.5 sm:pt-3.5">
          <div className="min-w-0 flex-1 py-0.5">
            <h2 id={titleId} className="truncate text-heading font-semibold tracking-tight">
              {title}
            </h2>
            {description && (
              <p id={descriptionId} className="mt-0.5 text-sm text-muted">
                {description}
              </p>
            )}
          </div>
          {headerAction && <div className="flex shrink-0 items-center self-center">{headerAction}</div>}
          <IconButton label="Close" size="sm" onClick={onClose} className="-mr-1 shrink-0 self-center">
            ✕
          </IconButton>
        </div>
        {children != null && children !== false && <div className="scroll-thin min-h-0 flex-1 overflow-auto p-5">{children}</div>}
        {(primary || secondary || cancelLabel) && (
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-line px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            {secondary}
            <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
              {cancelLabel && (
                <Button data-dialog-cancel onClick={onClose} disabled={busy}>
                  {cancelLabel}
                </Button>
              )}
              {primary}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** Slide-in panel from the left, used for navigation on small screens. */
export function Drawer({ open, onClose, children }: { open: boolean; onClose: () => void; children: ReactNode }) {
  const panel = useRef<HTMLDivElement>(null);
  useDialogLayer(open, onClose, panel);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-40 md:hidden">
      <div className="absolute inset-0 bg-black/25 backdrop-blur-sm" aria-hidden="true" onClick={onClose} />
      <div ref={panel} tabIndex={-1} className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col bg-canvas shadow-2xl outline-none">
        {children}
      </div>
    </div>,
    document.body,
  );
}

// ---- asking in a dialog: the browser's own confirm(), prompt() and alert() replaced, drawn by <DialogHost />.

type Ask =
  | { kind: "confirm"; title: string; body?: ReactNode; confirm: string; danger?: boolean; resolve: (yes: boolean) => void }
  | { kind: "prompt"; title: string; body?: ReactNode; label: string; value?: string; confirm: string; resolve: (value: string | null) => void }
  | { kind: "alert"; title: string; body?: ReactNode; resolve: () => void };

let show: ((ask: Ask) => void) | null = null;
let asked = 0;
const ask = (a: Ask) => {
  if (!show) throw new Error("<DialogHost /> is not mounted");
  show(a);
};

/** "Delete this?": resolves true when the person presses `confirm`, false on Cancel, Escape or the backdrop. */
export const confirmDialog = (o: { title: string; body?: ReactNode; confirm: string; danger?: boolean }) =>
  new Promise<boolean>((resolve) => ask({ kind: "confirm", ...o, resolve }));

/** One line of text asked for: resolves to what was typed, or null when cancelled. */
export const promptDialog = (o: { title: string; body?: ReactNode; label: string; value?: string; confirm: string }) =>
  new Promise<string | null>((resolve) => ask({ kind: "prompt", ...o, resolve }));

/** Something the person must read before going on: resolves when they close it. */
export const alertDialog = (o: { title: string; body?: ReactNode }) => new Promise<void>((resolve) => ask({ kind: "alert", ...o, resolve }));

/** Draws what confirmDialog, promptDialog and alertDialog ask, one over the other. Mounted once, beside the app. */
export function DialogHost() {
  const [asks, setAsks] = useState<{ id: number; ask: Ask }[]>([]);
  useEffect(() => {
    show = (a) => setAsks((list) => [...list, { id: ++asked, ask: a }]);
    return () => {
      show = null;
    };
  }, []);
  return (
    <>
      {asks.map(({ id, ask: a }) => (
        <AskDialog key={id} ask={a} onDone={() => setAsks((list) => list.filter((x) => x.id !== id))} />
      ))}
    </>
  );
}

function AskDialog({ ask: a, onDone }: { ask: Ask; onDone: () => void }) {
  const [value, setValue] = useState(a.kind === "prompt" ? (a.value ?? "") : "");
  const settle = (yes: boolean) => {
    if (a.kind === "confirm") a.resolve(yes);
    else if (a.kind === "prompt") a.resolve(yes ? value : null);
    else a.resolve();
    onDone();
  };
  const body = a.body && <p className="text-sm whitespace-pre-line text-muted">{a.body}</p>;
  if (a.kind === "alert") {
    return (
      <Dialog size="sm" onClose={() => settle(false)} title={a.title} primary={<Button variant="primary" data-autofocus onClick={() => settle(true)}>OK</Button>}>
        {body}
      </Dialog>
    );
  }
  if (a.kind === "prompt") {
    return (
      <Dialog
        size="sm"
        onClose={() => settle(false)}
        title={a.title}
        primary={<Button variant="primary" disabled={!value.trim()} onClick={() => settle(true)}>{a.confirm}</Button>}
      >
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (value.trim()) settle(true);
          }}
        >
          {body}
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-muted">{a.label}</span>
            <input className={inputCls} data-autofocus value={value} onChange={(e) => setValue(e.target.value)} />
          </label>
        </form>
      </Dialog>
    );
  }
  return (
    <Dialog
      size="sm"
      onClose={() => settle(false)}
      title={a.title}
      primary={<Button variant={a.danger ? "danger" : "primary"} onClick={() => settle(true)}>{a.confirm}</Button>}
    >
      {body}
    </Dialog>
  );
}
